/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import {
  Button,
  ButtonGroup,
  ButtonVariety,
  Card,
  Heading,
  Spinner,
  Text,
  ToggleTip,
} from '@sonarsource/echoes-react';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useJiraProjectsQuery, useJiraUserActionsQuery } from '../../queries';
import { JiraPermission, JiraProjectBinding } from '../../types';
import JiraProjectBindingDialog from './JiraProjectBindingDialog';
import JiraProjectDisconnectButton from './JiraProjectDisconnectButton';

interface JiraProjectBoundViewProps {
  orgBindingId?: string;
  orgId?: string;
  projectBinding: JiraProjectBinding;
  shouldCheckForBindingPermissions?: boolean;
  sonarProjectName: string;
}

export default function JiraProjectBoundView({
  orgId = '',
  orgBindingId = '',
  projectBinding,
  shouldCheckForBindingPermissions,
  sonarProjectName,
}: Readonly<JiraProjectBoundViewProps>) {
  const { data: jiraProjects = [], isLoading } = useJiraProjectsQuery(orgId);
  const jiraProjectName = useMemo(
    () =>
      jiraProjects.find((projects) => projects.key === projectBinding?.jiraProjectKey)?.name ?? '',
    [jiraProjects, projectBinding],
  );

  const {
    data: canBindProject = !shouldCheckForBindingPermissions,
    isLoading: isLoadingUserActions,
  } = useJiraUserActionsQuery(projectBinding.sonarProjectId, {
    select: (actions) => actions.includes(JiraPermission.BIND_PROJECT),
    enabled: shouldCheckForBindingPermissions === true,
  });

  const [bindingDialogOpen, setBindingDialogOpen] = useState(false);

  return (
    <>
      <Card className="sw-p-3">
        <Spinner isLoading={isLoading}>
          <div className="sw-items-center sw-flex sw-justify-between">
            <div>
              <Heading as="h3" className="sw-mb-3">
                <span className="fs-mask">{jiraProjectName}</span>
              </Heading>
              <Text isHighlighted>
                <FormattedMessage id="project_settings.jira_binding.key" />
              </Text>
              <Text className="sw-ml-1">
                <span className="fs-mask">{projectBinding.jiraProjectKey}</span>
              </Text>
            </div>
            <ButtonGroup>
              <Button
                isDisabled={isLoadingUserActions || !canBindProject}
                isLoading={isLoadingUserActions}
                onClick={() => {
                  setBindingDialogOpen(true);
                }}
                variety={ButtonVariety.Default}
              >
                <FormattedMessage id="edit" />
              </Button>
              <JiraProjectDisconnectButton
                jiraProjectName={jiraProjectName}
                projectBinding={projectBinding}
                shouldCheckForBindingPermissions={shouldCheckForBindingPermissions}
                sonarProjectName={sonarProjectName}
              />
              {!canBindProject && (
                <ToggleTip
                  description={
                    <FormattedMessage id="project_settings.jira_binding.bound.no_permissions" />
                  }
                />
              )}
            </ButtonGroup>
          </div>
        </Spinner>
      </Card>
      <JiraProjectBindingDialog
        isOpen={bindingDialogOpen}
        onClose={() => {
          setBindingDialogOpen(false);
        }}
        orgBindingId={orgBindingId}
        orgId={orgId}
        projectBinding={projectBinding}
        sonarProjectId={projectBinding.sonarProjectId}
        sonarProjectName={sonarProjectName}
      />
    </>
  );
}
